import type { AnswerAcceptanceLevel } from '@app/src/wf-constants'
import { ButtonBase, Slider, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { answerAcceptanceLevels } from './answerAcceptance'

interface AnswerAcceptanceSliderProps {
  level: AnswerAcceptanceLevel
  disabled: boolean
  onPreview: (level: AnswerAcceptanceLevel) => void
  onCommit: (level: AnswerAcceptanceLevel) => void
}

const marks = answerAcceptanceLevels.map((_, index) => ({ value: index }))
const lastMarkIndex = marks.length - 1

const AnswerAcceptanceSlider: React.FC<AnswerAcceptanceSliderProps> = ({ level, disabled, onPreview, onCommit }) => {
  const { formatMessage } = useIntl()
  const levelName = (index: number) =>
    formatMessage({ id: `aiScreening.acceptance.level.${answerAcceptanceLevels[index]}` })

  return (
    <Stack spacing={1}>
      <Stack px={1.5}>
        <Slider
          value={answerAcceptanceLevels.indexOf(level)}
          min={0}
          max={lastMarkIndex}
          step={null}
          marks={marks}
          track={false}
          disabled={disabled}
          aria-label={formatMessage({ id: 'aiScreening.acceptance.title' })}
          getAriaValueText={levelName}
          onChange={(_, index) => onPreview(answerAcceptanceLevels[index as number])}
          onChangeCommitted={(_, index) => onCommit(answerAcceptanceLevels[index as number])}
          sx={({ palette, shadows, transitions }) => {
            const thumbTransition = transitions.create(['left', 'box-shadow', 'transform'], {
              duration: 280,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            })
            return {
              height: 10,
              '& .MuiSlider-rail': {
                opacity: 0.15,
                bgcolor: palette.primary.main,
              },
              [`& .MuiSlider-mark[data-index="0"], & .MuiSlider-mark[data-index="${lastMarkIndex}"]`]: {
                display: 'none',
              },
              '& .MuiSlider-mark': {
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: palette.primary.main,
                opacity: 1,
              },
              '& .MuiSlider-thumb': {
                width: 28,
                height: 28,
                bgcolor: palette.background.paper,
                border: `3px solid ${palette.primary.main}`,
                boxShadow: shadows[3],
                transition: thumbTransition,
                '&:hover, &.Mui-focusVisible': { boxShadow: shadows[6] },
                '&.Mui-active': { transform: 'translate(-50%, -50%) scale(1.12)', boxShadow: shadows[8] },
                '&::before': { boxShadow: 'none' },
              },
              // MUI drops the transition while dragging; keeping it makes the thumb glide between notches.
              '&.MuiSlider-dragging .MuiSlider-thumb': { transition: thumbTransition },
              '&.Mui-disabled .MuiSlider-thumb': { borderColor: palette.text.disabled },
            }
          }}
        />
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        {answerAcceptanceLevels.map((option, index) => {
          const isSelected = option === level
          const alignment = index === 0 ? 'flex-start' : index === lastMarkIndex ? 'flex-end' : 'center'
          return (
            <ButtonBase
              key={option}
              disabled={disabled}
              onClick={() => onCommit(option)}
              sx={({ palette }) => ({
                borderRadius: 1,
                px: 1,
                py: 0.5,
                flex: 1,
                justifyContent: alignment,
                '&.Mui-focusVisible': { outline: `2px solid ${palette.primary.main}`, outlineOffset: 2 },
              })}
            >
              <Stack alignItems={alignment}>
                <Typography
                  variant={isSelected ? 'subtitle1' : 'body1'}
                  color={isSelected ? 'text.primary' : 'text.secondary'}
                  sx={({ transitions }) => ({ transition: transitions.create('color') })}
                >
                  {formatMessage({ id: `aiScreening.acceptance.level.${option}` })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatMessage({ id: `aiScreening.acceptance.levelSubtitle.${option}` })}
                </Typography>
              </Stack>
            </ButtonBase>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default AnswerAcceptanceSlider
